import { useEffect } from 'react';
import { useLocation } from '@/lib/router-compat';
import { trackPageView, initGA4 } from '@/lib/analytics';
import { hasAnalyticsConsent } from '@/lib/consent';
import { usePageTracking } from '@/hooks/usePageTracking';
import { isTrackableBrowserVisit } from '@/lib/analyticsHost';
import { sanitizeTrackedPath } from '../../supabase/functions/_shared/trackedPath';

const AnalyticsListener = () => {
  const location = useLocation();

  // GA4 is consent-gated. Initialise only if analytics consent is already
  // stored (src/lib/consent.ts owns that state and its expiry), otherwise
  // wait for the banner's consent-changed event. initGA4 is idempotent, so a
  // remount after acceptance cannot load gtag.js a second time.
  // Only the published site is measured: never the Lovable editor or a
  // preview host (src/lib/analyticsHost.ts).
  useEffect(() => {
    if (!isTrackableBrowserVisit()) return;
    if (hasAnalyticsConsent()) initGA4();
    const onConsentChanged = (e: Event) => {
      if ((e as CustomEvent).detail === 'accepted') initGA4();
    };
    window.addEventListener('consent-changed', onConsentChanged);
    return () => window.removeEventListener('consent-changed', onConsentChanged);
  }, []);

  // Track page views on route change (GA4). Same rule as the first-party
  // tracker: no portal paths, no token segments, no query beyond utm_*, no hash.
  useEffect(() => {
    if (!isTrackableBrowserVisit()) return;
    const path = sanitizeTrackedPath(location.pathname + location.search);
    if (path !== null) trackPageView(path);
  }, [location.pathname, location.search]);

  // First-party visitor analytics (consent-gated, anonymous)
  usePageTracking();

  return null;
};

export default AnalyticsListener;
